import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Bot, Code2, CreditCard, Gauge, KeyRound, MessageSquare, Network, ShieldCheck, SlidersHorizontal, Swords, Users, Workflow } from 'lucide-react';
import TopBar from '../../components/TopBar';
import PageTitle from '../../components/PageTitle';
import ContactFormModal from '../../components/ContactFormModal';
import SiteFooter from '../../components/SiteFooter';

const LLM_URL = import.meta.env.VITE_LLM_URL || 'https://llm.etherworks.cn';

const capabilityDefs = [
  { icon: Network, key: 'gateway', color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  { icon: SlidersHorizontal, key: 'model_control', color: 'text-violet-500', bg: 'bg-violet-500/10' },
  { icon: Swords, key: 'arena', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  { icon: BarChart3, key: 'usage', color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
];

const workflowDefs = [MessageSquare, Bot, Gauge, CreditCard];
const featureIcons = [Code2, KeyRound, ShieldCheck, Workflow, BarChart3, Swords];

const providerCards = [
  { name: 'GPT 5.5', tag: 'General' },
  { name: 'OPUS 4.8', tag: 'Reasoning' },
  { name: 'Gemini 3.1 Pro', tag: 'Multimodal' },
  { name: 'Task Coverage', tag: 'Coverage' },
  { name: 'Model Fit Guide', tag: 'Recommend' },
  { name: 'OpenAI-compatible API', tag: 'Integrate' },
];

export default function LlmPlatform() {
  const { t } = useTranslation('products');
  const [showContact, setShowContact] = useState(false);

  const features = featureIcons.map((icon, index) => ({
    icon,
    title: t(`llm_platform.feat${index + 1}_title`),
    desc: t(`llm_platform.feat${index + 1}_desc`),
  }));

  return (
    <div className="min-h-screen bg-background">
      <PageTitle titleKey="llm_platform.title" namespace="products" descriptionKey="llm_platform.subtitle" />
      <TopBar />

      <section className="relative pt-24 pb-8 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/[0.04] via-transparent to-transparent dark:from-cyan-500/[0.07]" />
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(6,182,212,0.08) 1px, transparent 0)', backgroundSize: '32px 32px' }} />

        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex h-16 w-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 items-center justify-center mb-6 shadow-lg shadow-cyan-500/20">
            <MessageSquare className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-4">
            <span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-emerald-500 bg-clip-text text-transparent">
              {t('llm_platform.name')}
            </span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground font-medium mb-8 max-w-2xl mx-auto">{t('llm_platform.subtitle')}</p>

          <div className="flex justify-center flex-wrap gap-4 mb-10">
            <a
              href={`${LLM_URL}/console`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/30 hover:scale-[1.02] active:scale-[0.98]"
            >
              {t('llm_platform.cta')}
              <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/plans"
              className="inline-flex items-center gap-2 px-8 py-3.5 border border-input bg-background hover:bg-accent hover:text-accent-foreground font-semibold rounded-xl transition-all"
            >
              {t('llm_platform.view_pricing')}
            </Link>
          </div>

          <div className="flex justify-center gap-8 flex-wrap">
            {[
              { value: '3', label: t('llm_platform.stat_arena') },
              { value: 'Coverage', label: t('llm_platform.stat_usage') },
              { value: 'OpenAI API', label: t('llm_platform.stat_api') },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col items-center">
                <span className="text-2xl font-bold text-foreground">{stat.value}</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pt-8 pb-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('llm_platform.capabilities_title')}</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">{t('llm_platform.capabilities_subtitle')}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {capabilityDefs.map((capability) => {
              const Icon = capability.icon;
              return (
                <div key={capability.key} className="group relative rounded-[1.25rem] border border-border p-2 md:p-3 transition-transform hover:scale-[1.02]">
                  <div className="relative flex h-full flex-col gap-3 overflow-hidden rounded-xl border border-border bg-background p-6 shadow-sm dark:shadow-[0px_0px_27px_0px_rgba(45,45,45,0.3)]">
                    <div className="flex items-center gap-3">
                      <div className={`h-9 w-9 rounded-lg ${capability.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${capability.color}`} />
                      </div>
                      <h3 className="text-base font-semibold text-foreground tracking-tight">{t(`llm_platform.cap_${capability.key}_title`)}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(`llm_platform.cap_${capability.key}_desc`)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('llm_platform.providers_title')}</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">{t('llm_platform.providers_subtitle')}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {providerCards.map((provider) => (
              <div key={provider.name} className="rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors">
                <div className="flex items-center justify-between mb-3 gap-3">
                  <span className="text-sm font-bold text-foreground">{provider.name}</span>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full whitespace-nowrap">{provider.tag}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{t('llm_platform.provider_desc')}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-12 text-center">{t('llm_platform.steps_title')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {workflowDefs.map((icon, index) => {
              const Icon = icon;
              const step = index + 1;
              return (
                <div key={step} className="text-center">
                  <div className="inline-flex h-12 w-12 rounded-full bg-primary/10 items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">{t(`llm_platform.step${step}_title`)}</h3>
                  <p className="text-sm text-muted-foreground">{t(`llm_platform.step${step}_desc`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('llm_platform.features_title')}</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">{t('llm_platform.features_subtitle')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.title} className="rounded-xl border border-border bg-card p-6 hover:border-primary/50 transition-colors">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-3xl mx-auto text-center">
          <Users className="w-10 h-10 text-primary mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3">{t('llm_platform.enterprise_title')}</h2>
          <p className="text-muted-foreground mb-8">{t('llm_platform.enterprise_subtitle')}</p>
          <button
            onClick={() => setShowContact(true)}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
          >
            {t('llm_platform.contact_sales')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <SiteFooter />

      {showContact && <ContactFormModal isOpen={showContact} onClose={() => setShowContact(false)} product="llm-platform" />}
    </div>
  );
}